import React, { useEffect, useRef, useState } from 'react';
import useCentreAutoplay from '../../hooks/useCentreAutoplay';

/** Whether the 360p loop suits this connection better than the 480p one. */
function slowConnection() {
  const type = typeof navigator !== 'undefined' ? navigator.connection?.effectiveType : null;
  return type === 'slow-2g' || type === '2g' || type === '3g';
}

/**
 * A video's poster that plays its 6-second muted preview loop while it is
 * the one nearest the centre of the screen (useCentreAutoplay; VIDEO_PLAN §12)
 * — for covers and the album hero, where a full player would be too much.
 * Never HLS: the preview is one small MP4.
 *
 * The poster stays underneath (stacked, §12) and the <video> fades in on its
 * first frame, so there's no black flash. When it stops being the one, the
 * <video> unmounts, which drops its download.
 *
 * @param {object}  playback  - { poster_url, poster_large_url, preview_url, preview_alt_url }
 * @param {boolean} [large]   - use the 1200 poster (hero surfaces)
 * @param {boolean} [enabled] - false: the poster only (e.g. a slide off screen)
 * @param {object}  [root]    - scroll container for the coordinator (default: the window)
 * @param {string}  [fit]     - 'cover' | 'contain'
 */
export default function VideoPreview({ playback, large = false, enabled = true, root = null, fit = 'cover', className = '', alt = '' }) {
  const boxRef = useRef(null);
  const src = playback ? ((slowConnection() && playback.preview_alt_url) || playback.preview_url) : null;
  const active = useCentreAutoplay(boxRef, { root, enabled: enabled && !!src });
  const [shown, setShown] = useState(false);
  useEffect(() => { if (!active) setShown(false); }, [active]);

  const poster = (large && playback?.poster_large_url) || playback?.poster_url || null;
  const objectFit = fit === 'contain' ? 'object-contain' : 'object-cover';

  // A caller that places the box (absolute inset-0 over a still) keeps its
  // position: with both classes, Tailwind's later `relative` would win and put
  // the preview in the flow, under the still it was meant to cover.
  const positioned = /(^|\s)(absolute|fixed|sticky)(\s|$)/.test(className);

  return (
    <div ref={boxRef} className={`${positioned ? '' : 'relative'} overflow-hidden ${className}`}>
      {poster && (
        <img src={poster} alt={alt} loading="lazy" draggable={false} className={`absolute inset-0 w-full h-full ${objectFit}`} />
      )}
      {active && src && (
        <video
          src={src}
          muted
          loop
          autoPlay
          playsInline
          preload="auto"
          aria-hidden="true"
          onPlaying={() => setShown(true)}
          className={`absolute inset-0 w-full h-full ${objectFit} transition-opacity duration-fast ${shown ? 'opacity-100' : 'opacity-0'}`}
        />
      )}
    </div>
  );
}
